import { useState, useEffect, useCallback } from "react";
import * as dateFns from "date-fns";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";
import utils from "@utils/useful-functions";
import { IOnFilterReturn } from "@organisms/GenericQueryFilter";

import { TravelAnalysisMetrics, TravelAnalysisFilter } from "../types";
import { DEFAULT_PERIOD_DAYS } from "../constants";

const USE_MOCK_DATA = false; // TODO: revert to `process.env.NODE_ENV === "development"` when needed

/** Mock payload for visual testing during development. */
const MOCK_METRICS: TravelAnalysisMetrics = {
  totalAnalyzed: 1284,
  successCount: 1102,
  successRate: 85.8,
  alertsByType: [
    { id: "136db83f-46a7-4837-970c-21e726fc978f", description: "Instabilidade de conexão GPRS", count: 47 },
    { id: "e2f32d7a-3076-4ba5-879e-f0333db7c232", description: "Informações de CAN travadas (completas ou parciais)", count: 38 },
    { id: "2f6349ac-719a-4b35-a161-45f513bb91e2", description: "Falha de posicionamento GPS", count: 31 },
    { id: "909443c5-7b7d-40a2-ae79-0534b90a8628", description: "Déficit de comandos de hardware enviados", count: 28 },
    { id: "0445b44e-9081-436e-904b-62bacdf2ec57", description: "Déficit de comandos de sensor ativo (IDLE elevado)", count: 22 },
    { id: "8abca603-c980-431c-8a67-cf8cbcebc754", description: "Inconsistência de fluxo logístico", count: 9 },
    { id: "c3db5f07-0adb-4647-ae8b-2380a898b95b", description: "Ciclo transversal (D+1)", count: 7 }
  ],
  period: {
    from: dateFns.subDays(new Date(), 30).toISOString(),
    to: new Date().toISOString()
  }
};

/** Return shape of the `useTravelAnalysis` hook. */
interface UseTravelAnalysisReturn {
  metrics: TravelAnalysisMetrics | null;
  loading: boolean;
  error: string | null;
  currentFilter: TravelAnalysisFilter | null;
  handleFilter: (filter: TravelAnalysisFilter) => Promise<IOnFilterReturn>;
}

/**
 * Orchestrates local state and API calls for the Travel Analysis dashboard.
 * Follows the same `useState` + `api.post` pattern as other report pages.
 */
const useTravelAnalysis = (): UseTravelAnalysisReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();

  const [metrics, setMetrics] = useState<TravelAnalysisMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilter, setCurrentFilter] = useState<TravelAnalysisFilter | null>(null);

  /**
   * Fetch metrics from the API with the given filter.
   * Called by `QueryFilterTravelAnalysis` via its `onFilter` callback.
   */
  const handleFilter = useCallback(async (filter: TravelAnalysisFilter): Promise<IOnFilterReturn> => {

    setLoading(true);
    setError(null);
    setCurrentFilter(filter);

    try {

      const { data } = await api.post("travels/analysis/metrics", filter);

      if (data.status === "success") {
        setMetrics(data.result);
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

      return { status: "success" } as IOnFilterReturn;

    } catch (err) {
      const message = err?.response?.data?.message || t(ToastMessages.connectionNotEstablished);

      setError(message);
      addToast({ type: "error", title: t(ToastMessages.error), description: message });

      return { status: "error" } as IOnFilterReturn;

    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fetch on mount with default 30-day period (or mock data in dev)
  useEffect(() => {
    if (USE_MOCK_DATA) {
      const now = new Date();
      const from = dateFns.subDays(now, DEFAULT_PERIOD_DAYS);

      setCurrentFilter({ from: from.toISOString(), to: now.toISOString() });
      setLoading(true);
      setTimeout(() => {
        setMetrics(MOCK_METRICS);
        setLoading(false);
      }, 800);

      return;
    }

    const now = new Date();
    const from = dateFns.subDays(now, DEFAULT_PERIOD_DAYS);

    from.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);

    handleFilter({
      from: utils.convertDateToISOWithTimezone(from),
      to: utils.convertDateToISOWithTimezone(now)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { metrics, loading, error, currentFilter, handleFilter };
};

export default useTravelAnalysis;
